/**
 * Sonner Toast Wrapper
 * 
 * This component implements a wrapper around the Sonner toast component
 * to ensure proper initialization order and prevent the
 * "Cannot access 'P' before initialization" error.
 */

// Import React from our singleton to ensure consistent React instance
import React from '@/utils/react-singleton';

// Log React availability for debugging
console.log('[SonnerWrapper] React initialization:', {
  version: React?.version || 'undefined',
  available: typeof React !== 'undefined',
  createContextAvailable: typeof React?.createContext === 'function'
});

// Fallback toast implementation
const fallbackToast: any = {
  success: (...args: any[]) => {},
  error: (...args: any[]) => {},
  info: (...args: any[]) => {},
  warning: (...args: any[]) => {},
  promise: (...args: any[]) => {},
  loading: (...args: any[]) => {},
  custom: (...args: any[]) => {},
  dismiss: (...args: any[]) => {}
};

// Component to handle async Sonner loading
export const Toaster = (props: any) => {
  const [SonnerComponent, setSonnerComponent] = React.useState<any>(null);
  
  React.useEffect(() => {
    // Load Sonner asynchronously
    import('sonner')
      .then(sonnerModule => {
        setSonnerComponent(() => sonnerModule.Toaster);
        console.log('[SonnerWrapper] Sonner loaded successfully:', {
          toasterAvailable: !!sonnerModule.Toaster
        });
      })
      .catch(error => {
        console.error('[SonnerWrapper] Error loading Sonner:', error);
        // Set a fallback component
        setSonnerComponent(() => ({ children }: { children?: React.ReactNode }) => <>{children}</>);
      });
  }, []);
  
  // Return null until Sonner is loaded
  if (!SonnerComponent) {
    return null;
  }
  
  return React.createElement(SonnerComponent, props);
};

// Export toast functions with dynamic loading
let loadedToast: any = fallbackToast;

// Load the real toast functions when Sonner is available
import('sonner')
  .then(sonnerModule => {
    if (sonnerModule.toast) {
      loadedToast = sonnerModule.toast;
    }
  })
  .catch(() => {
    // Keep using fallback
  });

export const toast: any = new Proxy(fallbackToast, {
  get(target: any, prop: any) {
    return loadedToast[prop] || target[prop];
  }
});

// Re-export useSonner hook if needed
export function useSonner() {
  const [toasts, setToasts] = React.useState([]);
  
  React.useEffect(() => {
    // Use dynamic import for sonner
    import('sonner')
      .then(sonnerModule => {
        if (sonnerModule.useSonner) {
          const result = sonnerModule.useSonner();
          if (result && result.toasts) {
            setToasts(result.toasts);
          }
        }
      })
      .catch(error => {
        console.error('[SonnerWrapper] Error using useSonner hook:', error);
      });
  }, []);
  
  return { toasts };
}
